import { useCallback, useMemo, useState } from "react";

import { UserRole } from "@eisox/api/core-api";
import { useAuthContext } from "@eisox/auth";
import dayjs from "@eisox/dayjs";
import { Button, CanAccess, Divider, Sidebar } from "@eisox/design-system";
import {
  AirHandlerUnitIcon,
  ArrowLeftIcon,
  BoilerRoomIcon,
  BuildingIcon,
  DashboardIcon,
  HelpIcon,
  LightingIcon,
  MapIcon,
  NutIcon,
  PlanIcon,
  RefreshIcon,
  SpannerIcon,
  UserIcon,
} from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useSuspenseQueries } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";

import {
  airHandlerUnitApi,
  authApi,
  boilerRoomApi,
  buildingApi,
  floorApi,
  gatewayApi,
  heatingNetworkApi,
  meshApi,
  moduleApi,
  multisensorApi,
  roomApi,
  valveApi,
} from "~/api";
import { HouseNameCard } from "~/components/house-name-card";
import { NewsDialog } from "~/components/news-dialog";
import { SidebarItem } from "~/components/sidebar-item";
import { Tooltip } from "~/components/tooltip";
import { getFirstSelectableAirHandlerUnit } from "~/features/air-handler-unit/helpers";
import { AlarmSidebarItem } from "~/features/alarms/components/alarm-sidebar-item";
import { buildingHasSupervisableModules } from "~/features/alarms/helpers";
import { getLastBuildingsMapSearch } from "~/features/buildings/helpers/buildings-map-search.helper";
import {
  CommandPalette,
  CommandPaletteTrigger,
  useCommandPaletteShortcut,
  type CommandPaletteSource,
} from "~/features/command-palette";
import { BOILER_ROOM_MODULE_TYPES, getPLCPageLink } from "~/features/module";
import { getPermissions } from "~/helpers/auth/auth.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { canSeeInstallerWarnings as resolveCanSeeInstallerWarnings } from "~/helpers/user.helper";
import { useUserPreferenceStore } from "~/integrations/zustand/stores/user-preference.store";
import { m } from "~/paraglide/messages.js";
import { BuildingProvider, useBuildingContext } from "~/providers/building.provider";
import { leaveIfOtherStack } from "~/utils/stack-guard.utils";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId")({
  beforeLoad: async ({ context: { queryClient }, params: { buildingId }, preload }) => {
    await leaveIfOtherStack(`/buildings/${buildingId}`, preload);
    const roles = await queryClient.ensureQueryData(authApi.queries.roles);
    const { permissions, role } = getPermissions(roles, buildingId);
    return { permissions, role };
  },
  loader: async ({ context: { queryClient, permissions }, params: { buildingId } }) => {
    const date = dayjs();
    const canReadModules = permissions.module?.boilerroom?.read || permissions.module?.airHandlerUnit?.read;
    const [boilerRooms, airHandlerUnits] = await Promise.all([
      permissions.module?.boilerroom?.read
        ? queryClient.ensureQueryData(boilerRoomApi.queries.building(buildingId))
        : Promise.resolve([]),
      permissions.module?.airHandlerUnit?.read
        ? queryClient.ensureQueryData(airHandlerUnitApi.queries.building(buildingId))
        : Promise.resolve([]),
      canReadModules ? queryClient.ensureQueryData(moduleApi.queries.building(buildingId)) : Promise.resolve([]),
      queryClient.ensureQueryData(buildingApi.queries.byId(buildingId)),
      queryClient.ensureQueryData(buildingApi.queries.graph(buildingId)),
      queryClient.ensureQueryData(floorApi.queries.building(buildingId)),
      queryClient.ensureQueryData(roomApi.queries.building(buildingId)),
      queryClient.ensureQueryData(valveApi.queries.building(buildingId)),
      queryClient.ensureQueryData(multisensorApi.queries.building(buildingId)),
      permissions.gateway?.read
        ? queryClient.ensureQueryData(gatewayApi.queries.building(buildingId))
        : Promise.resolve([]),
      permissions.mesh?.read ? queryClient.ensureQueryData(meshApi.queries.building(buildingId)) : Promise.resolve([]),
    ]);
    if (boilerRooms.length > 0 || airHandlerUnits.length > 0) {
      await queryClient.ensureQueryData(heatingNetworkApi.queries.building(buildingId));
    }
    return { date };
  },
  head: ({ match: { context }, params: { buildingId } }) => buildingPageHead(context.queryClient, buildingId),
  component: RouteComponent,
});

function Layout() {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const sidebarCollapsed = useUserPreferenceStore(s => s.sidebarCollapsed);
  const toggleSidebar = useUserPreferenceStore(s => s.toggleSidebar);
  const advancedMaintenance = useUserPreferenceStore(s => s.maintenance.advanced);

  const {
    building,
    floors,
    rooms,
    valves,
    boilerRooms,
    airHandlerUnits,
    heatingNetworks,
    gateways,
    modules,
    multisensors,
    role,
    permissions,
    canSeeInstallerWarnings,
  } = useBuildingContext(Route.id);
  const buildingId = building.id;
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);

  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen(previousOpen => !previousOpen);
  }, []);

  useCommandPaletteShortcut({ onTrigger: toggleCommandPalette, enabled: !isMobile });

  const hasSupervisableModules = buildingHasSupervisableModules({ boilerRooms, airHandlerUnits, modules });

  const { user, signOut, token } = useAuthContext(Route.id);

  const proxyEnabledBoilerRoomModules = modules.filter(
    md => BOILER_ROOM_MODULE_TYPES.includes(md.type) && md.proxy?.isEnabled,
  );
  const singleProxyModule = proxyEnabledBoilerRoomModules[0];
  const gtbInNewWindow = proxyEnabledBoilerRoomModules.length === 1 && !!singleProxyModule?.proxy?.isExternal;
  // Set only when the BMS really opens in a new window, so it doubles as that flag downstream.
  // `gtbInNewWindow` already narrows `singleProxyModule` to a defined module.
  const gtbNewWindowLink =
    gtbInNewWindow && token ? getPLCPageLink(token, buildingId, singleProxyModule.id) : undefined;

  const canAccessAlarms = !!permissions.page?.alarms;
  const canAccessAirHandlerUnits =
    !!permissions.page?.airHandlerUnit && !!airHandlerUnits.length && building.pages.airHandlerUnits;
  const canAccessBoilerRoom = !!permissions.page?.boilerroom && !!boilerRooms.length && building.pages.boilerRooms;
  const firstAirHandlerUnit = getFirstSelectableAirHandlerUnit(airHandlerUnits);
  const canAccessGTB = !!permissions.page?.gtb && modules.some(md => md.proxy?.isEnabled);
  const canAccessConsumption = !!permissions.page?.consumption;
  const canAccessUsers = !!permissions.page?.users;
  const canAccessMaintenance = !!permissions.page?.maintenance;
  const canAccessRessources = !!permissions.page?.ressource;
  const canAccessSettings = !!permissions.page?.settings;

  const displayChristmasHat = role === UserRole.INSTALLER && dayjs().month() === 11;

  const handleRedirect = () => {
    if (location.pathname) {
      window.location.assign(
        (import.meta.env.MODE === "staging" || import.meta.env.MODE === "pre-prod"
          ? import.meta.env.VITE_PROD_WEBAPP_URL
          : import.meta.env.VITE_PREPROD_WEBAPP_URL) + location.pathname,
      );
    }
  };

  const buildingsMapSearch = getLastBuildingsMapSearch();

  const commandPaletteSource = useMemo(
    (): CommandPaletteSource => ({
      buildingId,
      building,
      floors,
      rooms,
      valves,
      boilerRooms,
      airHandlerUnits,
      heatingNetworks,
      gateways,
      modules,
      multisensors,
      permissions,
      canSeeInstallerWarnings,
      access: {
        canAccessAlarms,
        canAccessAirHandlerUnits,
        canAccessBoilerRoom,
        canAccessGTB,
        canAccessConsumption,
        canAccessUsers,
        canAccessMaintenance,
        canAccessRessources,
        canAccessSettings,
        advancedMaintenance,
        gtbNewWindowLink,
        isMobile,
      },
    }),
    [
      advancedMaintenance,
      airHandlerUnits,
      boilerRooms,
      building,
      buildingId,
      canSeeInstallerWarnings,
      canAccessAirHandlerUnits,
      canAccessAlarms,
      canAccessBoilerRoom,
      canAccessConsumption,
      canAccessGTB,
      canAccessMaintenance,
      canAccessRessources,
      canAccessSettings,
      canAccessUsers,
      floors,
      gateways,
      gtbNewWindowLink,
      heatingNetworks,
      isMobile,
      modules,
      multisensors,
      permissions,
      rooms,
      valves,
    ],
  );

  return (
    <Sidebar.Provider collapsed={sidebarCollapsed} onCollapsedChange={toggleSidebar}>
      <Sidebar.Root displayChristmasHat={displayChristmasHat}>
        <Sidebar.Header className="mt-0 mb-1.25 max-w-42.5 gap-3.75 mx-auto flex flex-col items-center">
          {({ collapsed }) => (
            <>
              {collapsed ? (
                <Tooltip content={m.my_buildings()} position="right" sideOffset={20} arrow={false}>
                  <Link to="/buildings" search={buildingsMapSearch}>
                    <MapIcon className="h-5.25 w-5.25 text-primary-dark-gray" />
                  </Link>
                </Tooltip>
              ) : (
                <Button asChild className="font-semibold text-primary-dark-gray">
                  <Link to="/buildings" search={buildingsMapSearch}>
                    <ArrowLeftIcon />
                    {m.my_buildings()}
                  </Link>
                </Button>
              )}
              <Divider className="my-0 max-w-18 mx-auto w-full" />
              <HouseNameCard
                name={building.name}
                collapsed={collapsed}
                suspended={building.suspendedAt !== undefined}
              />
            </>
          )}
        </Sidebar.Header>
        <Sidebar.Content>
          <SidebarItem to="/buildings/$buildingId/dashboard" params={{ buildingId }}>
            <DashboardIcon />
            {m.dashboard()}
          </SidebarItem>
          <SidebarItem to="/buildings/$buildingId/floors" params={{ buildingId }}>
            <PlanIcon />
            {m.plan()}
          </SidebarItem>
          {canAccessAlarms && (
            <AlarmSidebarItem
              buildingId={buildingId}
              isInstaller={role === UserRole.INSTALLER}
              hasSupervisableModules={hasSupervisableModules}
            />
          )}
          {!isMobile && canAccessBoilerRoom && (
            <SidebarItem to="/buildings/$buildingId/boiler-rooms" params={{ buildingId }}>
              <BoilerRoomIcon />
              {m.boiler_room()}
            </SidebarItem>
          )}
          {!isMobile && canAccessAirHandlerUnits && firstAirHandlerUnit && (
            <SidebarItem
              to="/buildings/$buildingId/air-handler-units/$airHandlerUnitId"
              params={{ buildingId, airHandlerUnitId: firstAirHandlerUnit.id }}
            >
              <AirHandlerUnitIcon />
              {m.air_handler_units()}
            </SidebarItem>
          )}
          {canAccessGTB &&
            (gtbInNewWindow ? (
              <Sidebar.Item asChild>
                <a href={gtbNewWindowLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <BuildingIcon />
                  {m.bms()}
                </a>
              </Sidebar.Item>
            ) : (
              <SidebarItem to="/buildings/$buildingId/bms" params={{ buildingId }}>
                <BuildingIcon />
                {m.bms()}
              </SidebarItem>
            ))}
          {canAccessConsumption && (
            <SidebarItem to="/buildings/$buildingId/consumption" params={{ buildingId }}>
              <LightingIcon />
              {m.consumption()}
            </SidebarItem>
          )}
          {!isMobile && canAccessUsers && (
            <SidebarItem to="/buildings/$buildingId/users" params={{ buildingId }}>
              <UserIcon />
              {m.users()}
            </SidebarItem>
          )}
          {canAccessMaintenance && (
            <SidebarItem
              to={
                advancedMaintenance
                  ? "/buildings/$buildingId/maintenance/advanced"
                  : "/buildings/$buildingId/maintenance"
              }
              activeTo="/buildings/$buildingId/maintenance"
              params={{ buildingId }}
            >
              <SpannerIcon />
              {m.maintenance()}
            </SidebarItem>
          )}
          {!isMobile && canAccessSettings && (
            <SidebarItem
              to="/buildings/$buildingId/settings/buildings/general"
              activeTo="/buildings/$buildingId/settings"
              params={{ buildingId }}
            >
              <NutIcon />
              {m.settings()}
            </SidebarItem>
          )}
          <div className="gap-3.75 mt-auto flex flex-col">
            {canAccessRessources && (
              <SidebarItem to="/buildings/$buildingId/resources/$" params={{ buildingId, _splat: "" }}>
                <HelpIcon />
                {m.assistance()}
              </SidebarItem>
            )}
            {!isMobile && (
              <CommandPaletteTrigger collapsed={sidebarCollapsed} onClick={() => setCommandPaletteOpen(true)} />
            )}
          </div>
        </Sidebar.Content>
        <Sidebar.Footer
          email={user?.email ?? ""}
          version={import.meta.env.VITE_VERSION}
          firstName={user?.firstName}
          lastName={user?.lastName}
          role={m[role]()}
        >
          <div className="gap-2.5 flex flex-col items-start">
            <CanAccess isAuthorized={role === UserRole.INSTALLER}>
              {(import.meta.env.MODE === "staging" ||
                import.meta.env.MODE === "pre-prod" ||
                import.meta.env.MODE === "prod") && (
                <Button onClick={handleRedirect} className={"text-primary-dark-blue"}>
                  {import.meta.env.MODE.toUpperCase()}
                  <RefreshIcon />
                </Button>
              )}
            </CanAccess>
            <Button className="text-primary-dark-blue" onClick={() => setNewsOpen(true)}>
              {m.new()}
            </Button>
            <Button asChild className="text-primary-dark-blue">
              <Link to="/account">{m.my_acccount()}</Link>
            </Button>
            <Button
              className="text-primary-dark-blue"
              onClick={() => {
                void signOut();
              }}
            >
              {m.sign_out()}
            </Button>
          </div>
        </Sidebar.Footer>
      </Sidebar.Root>
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} source={commandPaletteSource} />
      <NewsDialog open={newsOpen} onOpenChange={setNewsOpen} canReadMore={canAccessRessources} />
      <Outlet />
    </Sidebar.Provider>
  );
}

function RouteComponent() {
  const { permissions, role } = Route.useRouteContext();
  const { user } = useAuthContext(Route.id);
  const { buildingId } = Route.useParams();
  const { date } = Route.useLoaderData();

  const canSeeInstallerWarnings = resolveCanSeeInstallerWarnings(role, user?.features);
  const skip = (resource: string) => ({
    queryKey: ["skipped", resource, buildingId],
    queryFn: () => [],
  });

  const [
    { data: building },
    { data: graph },
    { data: floors },
    { data: rooms },
    { data: valves },
    { data: multisensors },
    { data: gateways },
    { data: meshes },
    { data: boilerRooms },
    { data: airHandlerUnits },
  ] = useSuspenseQueries({
    queries: [
      buildingApi.queries.byId(buildingId),
      buildingApi.queries.graph(buildingId),
      floorApi.queries.building(buildingId),
      roomApi.queries.building(buildingId),
      valveApi.queries.building(buildingId),
      multisensorApi.queries.building(buildingId),
      permissions.gateway?.read ? gatewayApi.queries.building(buildingId) : skip("gateway"),
      permissions.mesh?.read ? meshApi.queries.building(buildingId) : skip("mesh"),
      permissions.module?.boilerroom?.read ? boilerRoomApi.queries.building(buildingId) : skip("boilerRoom"),
      permissions.module?.airHandlerUnit?.read
        ? airHandlerUnitApi.queries.building(buildingId)
        : skip("airHandlerUnit"),
    ],
  });

  const canReadModules = permissions.module?.boilerroom?.read || permissions.module?.airHandlerUnit?.read;
  const hasBoilerRoomOrAhu = boilerRooms.length > 0 || airHandlerUnits.length > 0;
  const [{ data: heatingNetworks }, { data: modules }] = useSuspenseQueries({
    queries: [
      hasBoilerRoomOrAhu ? heatingNetworkApi.queries.building(buildingId) : skip("heatingNetwork"),
      canReadModules ? moduleApi.queries.building(buildingId) : skip("module"),
    ],
  });

  return (
    <BuildingProvider
      role={role}
      permissions={permissions}
      canSeeInstallerWarnings={canSeeInstallerWarnings}
      building={building}
      graph={graph}
      floors={floors}
      rooms={rooms}
      gateways={gateways}
      meshes={meshes}
      valves={valves}
      multisensors={multisensors}
      boilerRooms={boilerRooms}
      airHandlerUnits={airHandlerUnits}
      heatingNetworks={heatingNetworks}
      modules={modules}
      date={date}
    >
      <Layout />
    </BuildingProvider>
  );
}
